import { useEffect, useRef } from 'react'

import {
  bindAnchoredOverlayPosition,
} from '../DatePicker/anchorPositioning'

import { TableColumn } from './types'

// Update dropdown position
export function useDropdownPositionEffect(
  openDropdown: { rowIndex: number; columnKey: string } | null,
  data: unknown[],
  columns: TableColumn[],
  dropdownRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  setDropdownPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number } | null>>
) {
  useEffect(() => {
    if (!openDropdown) {
      setDropdownPosition(null)
      return
    }

    const updatePosition = () => {
      const cellKey = `${openDropdown.rowIndex}-${openDropdown.columnKey}`
      const cell = dropdownRefs.current[cellKey]
      if (!cell) return

      const rect = cell.getBoundingClientRect()
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }

    updatePosition()
    const timeoutId = setTimeout(updatePosition, 100)

    window.addEventListener('scroll', updatePosition, true)
    window.addEventListener('resize', updatePosition)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('scroll', updatePosition, true)
      window.removeEventListener('resize', updatePosition)
    }
  }, [openDropdown, data, columns, dropdownRefs, setDropdownPosition])
}

// Update DatePicker position
export function useDatePickerPositionEffect(
  openDatePicker: { rowIndex: number; columnKey: string } | null,
  data: unknown[],
  columns: TableColumn[],
  datePickerRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  tableScrollContainerRef: React.RefObject<HTMLDivElement>,
  datePickerElementRef: React.RefObject<HTMLDivElement>,
  setDatePickerPosition: React.Dispatch<React.SetStateAction<{ top: number; left: number; width: number } | null>>
) {
  const columnsRef = useRef(columns)
  columnsRef.current = columns

  useEffect(() => {
    if (!openDatePicker) {
      setDatePickerPosition(null)
      return
    }

    const cellKey = `${openDatePicker.rowIndex}-${openDatePicker.columnKey}`

    return bindAnchoredOverlayPosition({
      getTrigger: () => datePickerRefs.current[cellKey] ?? null,
      getPicker: () => datePickerElementRef.current,
      extraScrollTargets: [tableScrollContainerRef.current],
      options: {
        resolveLeft: (rect, pickerWidth, viewportWidth) => {
          const column = columnsRef.current.find((col) => col.key === openDatePicker.columnKey)
          const includeTime = column?.datePicker?.includeTime || false
          const rightEdge = rect.right
          const leftEdge = rect.left

          if (includeTime) {
            if (rightEdge - pickerWidth >= 0) return rightEdge - pickerWidth
            if (leftEdge + pickerWidth <= viewportWidth) return leftEdge
            return Math.max(0, viewportWidth - pickerWidth)
          }

          if (leftEdge + pickerWidth <= viewportWidth) return leftEdge
          if (rightEdge - pickerWidth >= 0) return rightEdge - pickerWidth
          return Math.max(0, viewportWidth - pickerWidth)
        },
        pickerWidth: (() => {
          const cell = datePickerRefs.current[cellKey]
          const rect = cell?.getBoundingClientRect()
          const column = columnsRef.current.find((col) => col.key === openDatePicker.columnKey)
          const includeTime = column?.datePicker?.includeTime || false
          return includeTime ? 600 : Math.max(rect?.width ?? 0, 280)
        })(),
        includeTime: columnsRef.current.find((col) => col.key === openDatePicker.columnKey)?.datePicker
          ?.includeTime,
      },
      onPosition: (position) => {
        setDatePickerPosition({
          top: position.top,
          left: position.left,
          width: position.width,
        })
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- anchor only when cell changes
  }, [openDatePicker?.rowIndex, openDatePicker?.columnKey, datePickerRefs, tableScrollContainerRef, datePickerElementRef, setDatePickerPosition])
}

// Close dropdown/datepicker when clicking outside
export function useClickOutsideEffect(
  openDropdown: { rowIndex: number; columnKey: string } | null,
  openDatePicker: { rowIndex: number; columnKey: string } | null,
  dropdownRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  datePickerRefs: React.MutableRefObject<{ [key: string]: HTMLTableCellElement | null }>,
  dropdownElementRef: React.RefObject<HTMLDivElement>,
  datePickerElementRef: React.RefObject<HTMLDivElement>,
  setOpenDropdown: React.Dispatch<React.SetStateAction<{ rowIndex: number; columnKey: string } | null>>,
  setOpenDatePicker: React.Dispatch<React.SetStateAction<{ rowIndex: number; columnKey: string } | null>>
) {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node

      // Check dropdown
      if (openDropdown) {
        const cellKey = `${openDropdown.rowIndex}-${openDropdown.columnKey}`
        const cell = dropdownRefs.current[cellKey]
        const dropdown = dropdownElementRef.current

        if (cell && !cell.contains(target) && dropdown && !dropdown.contains(target)) {
          setOpenDropdown(null)
        }
      }

      // Check DatePicker
      if (openDatePicker) {
        const cellKey = `${openDatePicker.rowIndex}-${openDatePicker.columnKey}`
        const cell = datePickerRefs.current[cellKey]
        const datePicker = datePickerElementRef.current

        if (cell && !cell.contains(target) && datePicker && !datePicker.contains(target)) {
          setOpenDatePicker(null)
        }
      }
    }

    if (openDropdown || openDatePicker) {
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside)
      }, 0)

      return () => {
        clearTimeout(timeoutId)
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }
  }, [openDropdown, openDatePicker, dropdownRefs, datePickerRefs, dropdownElementRef, datePickerElementRef, setOpenDropdown, setOpenDatePicker])
}

// Lazy loading: Intersection Observer
export function useLazyLoadingEffect(
  lazyLoading: { enabled: boolean; initialCount?: number; loadMoreThreshold?: number; onLoadMore?: () => Promise<void> | void; isLoadingMore?: boolean } | undefined,
  allLoaded: boolean,
  dataLength: number,
  displayedCount: number,
  setDisplayedCount: React.Dispatch<React.SetStateAction<number>>,
  setAllLoaded: React.Dispatch<React.SetStateAction<boolean>>,
  loadMoreTriggerRef: React.RefObject<HTMLTableRowElement>
) {
  // Use refs to avoid dependency on displayedCount and dataLength which change frequently
  const displayedCountRef = useRef<number>(displayedCount)
  const dataLengthRef = useRef<number>(dataLength)
  
  // Update refs on every render to keep them in sync
  displayedCountRef.current = displayedCount
  dataLengthRef.current = dataLength
  
  useEffect(() => {
    if (!lazyLoading?.enabled || allLoaded || lazyLoading.isLoadingMore) {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const threshold = lazyLoading.loadMoreThreshold || 0.7
          if (entries[0].intersectionRatio >= threshold) {
            setDisplayedCount((prev) => {
              const next = prev + (lazyLoading.initialCount || 20)
              const currentDataLength = dataLengthRef.current
              if (next >= currentDataLength) {
                setAllLoaded(true)
                return currentDataLength
              }
              return next
            })
            if (lazyLoading.onLoadMore) {
              lazyLoading.onLoadMore()
            }
          }
        }
      },
      { threshold: lazyLoading.loadMoreThreshold || 0.7 }
    )

    if (loadMoreTriggerRef.current) {
      observer.observe(loadMoreTriggerRef.current)
    }

    return () => {
      if (loadMoreTriggerRef.current) {
        observer.unobserve(loadMoreTriggerRef.current)
      }
    }
  }, [lazyLoading, allLoaded, setDisplayedCount, setAllLoaded, loadMoreTriggerRef])
}
